import type { CloverExtensionTokens } from '@/services/cloverExtension'
import type { BackgroundJobStatus } from '@/services/jobs'

/** Credential keys sent on PUT /external-credentials/clover */
export const CLOVER_PUT_CREDENTIAL_KEYS = [
  'sessionToken',
  'sessionContext',
  'csrfToken',
  'giftcardsPlaySession',
  'giftcardsAuthToken',
] as const

export type CloverPutCredentials = Partial<
  Record<(typeof CLOVER_PUT_CREDENTIAL_KEYS)[number], string>
>

export function buildCloverPutPayload(tokens: CloverExtensionTokens): CloverPutCredentials {
  return {
    sessionToken: tokens.sessionToken,
    sessionContext: tokens.sessionContext,
    csrfToken: tokens.csrfToken,
    ...(tokens.giftcardsPlaySession
      ? { giftcardsPlaySession: tokens.giftcardsPlaySession }
      : {}),
    ...(tokens.giftcardsAuthToken ? { giftcardsAuthToken: tokens.giftcardsAuthToken } : {}),
  }
}

/** True when any PUT credential key differs (missing vs present counts as a change). */
export function cloverTokensChanged(
  prev: CloverPutCredentials | null,
  next: CloverPutCredentials,
): boolean {
  for (const key of CLOVER_PUT_CREDENTIAL_KEYS) {
    const previous = prev?.[key]
    const incoming = next[key]
    if (previous !== incoming) {
      if (previous === undefined && incoming === undefined) continue
      return true
    }
  }
  return false
}

/** After a job ends, keepalive stays on only when the user pinned it. */
export function shouldKeepaliveOnAfterJob(keepalivePinned: boolean, jobActive: boolean): boolean {
  if (jobActive) return true
  return keepalivePinned
}

export function cloverJobStatusLabel(status: BackgroundJobStatus): string {
  switch (status) {
    case 'pending':
      return 'queued'
    case 'running':
      return 'running'
    case 'waiting_for_clover_tokens':
      return 'waiting for Clover tokens'
    case 'paused_token_timeout':
      return 'paused (token timeout)'
    case 'completed':
      return 'completed'
    case 'failed':
      return 'failed'
    default:
      return status
  }
}

export const CLOVER_SESSION_EXPIRED_BANNER =
  'Clover session expired — log in to Clover in the other tab; tokens will be picked up automatically'

export function extensionMissingMessage(): string {
  return 'Clover extension is not installed.'
}

export function noCloverTabMessage(): string {
  return 'No Clover tab open.'
}

export function formatKeepaliveTimestamp(value?: string | number | null): string | null {
  if (value == null || value === '') return null
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return typeof value === 'string' ? value : null
  }
}

export function hostLoginSummary(hosts: { www: boolean; giftcards: boolean }): string {
  const parts: string[] = []
  if (hosts.www) parts.push('clover.com logged in')
  else parts.push('clover.com logged out')
  if (hosts.giftcards) parts.push('giftcards logged in')
  else parts.push('giftcards logged out')
  return parts.join(' · ')
}
